import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type Ref,
} from "react";
import { cn } from "../cn";
import { Icon, type IconName } from "./icon";

interface Handle {
  startAnimation(this: void): void;
  stopAnimation(this: void): void;
}
type Animated = ComponentType<{ ref?: Ref<Handle>; size?: number }>;
type Marks = Partial<Record<IconName, Animated>>;

let marks: Marks | undefined;
let loading_: Promise<void> | undefined;

/** Fetch the animated marks once, off the path to first paint. */
function loadMarks(): Promise<void> {
  loading_ ??= import("../icons/animated").then((m) => {
    marks = m.MARKS as Marks;
  });
  return loading_;
}

function useMarks(): Marks | undefined {
  const [ready, setReady] = useState(marks);
  useEffect(() => {
    if (marks) return;
    let live = true;
    const go = () => void loadMarks().then(() => live && setReady(marks));
    const idle = "requestIdleCallback" in window;
    const id = idle ? window.requestIdleCallback(go) : window.setTimeout(go, 200);
    return () => {
      live = false;
      if (idle) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);
  return ready;
}

export interface ButtonIconProps {
  name: IconName;
  /** The action succeeded: the mark becomes a tick that draws itself. */
  done?: boolean;
  /** Working: a refresh mark spins for as long as this holds. */
  loading?: boolean;
  /** Pixel size; the working size when left out. */
  size?: number;
  className?: string;
}

const still = (el: Element) =>
  getComputedStyle(el).getPropertyValue("--motion-k").trim() === "0";

/**
 * The icon a button carries, a Lucide Animated mark that plays its own
 * animation while the button is hovered or pressed, draws itself when `done`,
 * and spins while `loading`. The host is the nearest `.mw-press` control, button or link.
 * Nothing plays under reduced motion (`--motion-k` is 0).
 */
export function ButtonIcon({ name, done, loading, size, className }: ButtonIconProps) {
  const shown: IconName = done ? "check" : name;
  const set = useMarks();
  const Mark = set?.[shown];
  const box = useRef<HTMLSpanElement>(null);
  const handle = useRef<Handle>(null);

  useEffect(() => {
    const el = box.current;
    const host = el?.closest<HTMLElement>(".mw-press, button, a, [role=button]");
    if (!el || !host) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const idle = () =>
      host.matches(":disabled") || host.getAttribute("aria-disabled") === "true";
    const play = () => {
      if (!idle() && !still(el)) handle.current?.startAnimation();
    };
    const rest = () => handle.current?.stopAnimation();
    const press = (e: PointerEvent) => {
      play();
      if (e.pointerType !== "mouse") {
        clearTimeout(timer);
        timer = setTimeout(rest, 700);
      }
    };
    host.addEventListener("mouseenter", play);
    host.addEventListener("mouseleave", rest);
    host.addEventListener("pointerdown", press);
    host.addEventListener("focus", play);
    host.addEventListener("blur", rest);
    return () => {
      clearTimeout(timer);
      host.removeEventListener("mouseenter", play);
      host.removeEventListener("mouseleave", rest);
      host.removeEventListener("pointerdown", press);
      host.removeEventListener("focus", play);
      host.removeEventListener("blur", rest);
    };
  }, [shown, Mark]);

  useEffect(() => {
    const el = box.current;
    if (done && el && !still(el)) handle.current?.startAnimation();
  }, [done, Mark]);

  const spin = loading && name === "refresh";
  return (
    <span
      key={shown}
      ref={box}
      aria-hidden="true"
      data-icon={done ? undefined : name}
      data-done={done ? "" : undefined}
      style={size ? ({ "--mw-bi": `${size}px` } as CSSProperties) : undefined}
      className={cn("mw-bi", className)}
    >
      <span className={cn("inline-grid", spin && "motion-safe:animate-spin")}>
        {Mark ? (
          <Mark ref={handle} size={size ?? 16} />
        ) : (
          <Icon name={shown} className={size ? "size-[var(--mw-bi)]" : undefined} />
        )}
      </span>
    </span>
  );
}
